import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const HeroSection = styled.section`
  position: relative;
  padding: 80px 24px 100px;
  background: linear-gradient(180deg, #ffffff 0%, #f0f4f8 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const LeftContent = styled.div`
  z-index: 2;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background-color: rgba(0, 33, 71, 0.05);
  color: #002147;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  border: 1px solid rgba(0, 33, 71, 0.1);
`;

const Title = styled.h1`
  font-size: 56px;
  line-height: 1.1;
  margin-bottom: 24px;
  color: #002147;
  font-weight: 900;

  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  line-height: 1.6;
  color: #4a4a4a;
  margin-bottom: 40px;
  max-width: 540px;

  @media (max-width: 960px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  
  @media (max-width: 960px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  background-color: #002147;
  color: white;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  font-size: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 33, 71, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 33, 71, 0.3);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  background-color: white;
  color: #002147;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  font-size: 16px;
  border: 2px solid rgba(0, 33, 71, 0.1);
  transition: all 0.2s ease;

  &:hover {
    border-color: #002147;
    background-color: #f8f9fa;
  }
`;

const RightContent = styled.div`
  position: relative;
  z-index: 1;
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.4) 100%);
    z-index: 1;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transform: scale(1.02);
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  bottom: 40px;
  left: -40px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  width: 320px;
  z-index: 3;
  border-left: 4px solid #005f73;

  @media (max-width: 960px) {
    left: 50%;
    transform: translateX(-50%) !important;
    bottom: -30px;
  }
`;

const ChatMessage = styled.div<{ isUser?: boolean }>`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  align-items: flex-start;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
`;

const Avatar = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const Bubble = styled.div<{ isUser?: boolean }>`
  background-color: ${props => props.isUser ? '#f0f4f8' : '#f8f9fa'};
  padding: 10px 14px;
  border-radius: 12px;
  border-bottom-left-radius: ${props => props.isUser ? '12px' : '2px'};
  border-bottom-right-radius: ${props => props.isUser ? '2px' : '12px'};
  font-size: 13px;
  line-height: 1.5;
  color: #1a1a1a;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const TrustedBy = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  
  p {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #6b7280;
    margin-bottom: 20px;
    font-weight: 600;
  }
`;

const LogoGrid = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  opacity: 0.7;
  flex-wrap: wrap;
  
  @media (max-width: 960px) {
    justify-content: center;
  }

  img {
    height: 32px;
    filter: grayscale(100%);
    transition: filter 0.3s;
    
    &:hover {
      filter: grayscale(0%);
    }
  }
`;

export const EducationHero: React.FC = () => {
  return (
    <HeroSection>
      <Container>
        <LeftContent>
          <Badge>New: Canvas & Moodle Integration</Badge>
          <Title>The 24/7 Admissions Assistant for Modern Institutes</Title>
          <Subtitle>
            Automate student queries, qualify enrollments, and let your admissions team focus on teaching—not answering the same emails 50 times a day.
          </Subtitle>
          <ButtonGroup>
            <PrimaryButton href="/get-started">Start Free Pilot</PrimaryButton>
            <SecondaryButton href="#demo">View Interactive Demo</SecondaryButton>
          </ButtonGroup>

          <TrustedBy>
            <p>Trusted by leading institutes</p>
            <LogoGrid>
              <img src="/Clients/favicon-32x32 (1).png" alt="TEFL Institute" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
              <img src="/Clients/favicon-32x32.png" alt="Premier TEFL" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
              <img src="/Clients/background-32x32.webp" alt="University of Essex" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
            </LogoGrid>
          </TrustedBy>
        </LeftContent>

        <RightContent>
          <ImageWrapper>
            {/* Using a placeholder image that looks academic */}
            <HeroImage
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
              alt="Student studying on campus"
            />
          </ImageWrapper>

          <FloatingCard
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <ChatMessage isUser>
              <Bubble isUser>Do I need a degree to enroll in the Level 5 course?</Bubble>
              <Avatar color="#005f73">S</Avatar>
            </ChatMessage>
            <ChatMessage>
              <Avatar color="#002147">AI</Avatar>
              <Bubble>
                No, a degree is not required! You just need to be a native English speaker or have C1 proficiency. Would you like to see the full syllabus?
              </Bubble>
            </ChatMessage>
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#005f73', fontWeight: 600, background: '#e0f2f1', padding: '4px 8px', borderRadius: '4px' }}>✓ Eligibility Checked</span>
            </div>
          </FloatingCard>
        </RightContent>
      </Container>
    </HeroSection>
  );
};

export default EducationHero;
